/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import PropTypes from 'prop-types';

// Component imports
import { Avatar, Button, Col, Input, List, Radio, Row, Tooltip, Typography } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, SearchOutlined, TeamOutlined } from '@ant-design/icons';

// Misc imports
import formatEndUserName from '../../utils/string/formatEndUserName';
import { transferConversation } from '../../store/liveChat/liveChatThunks';
import { LIVECHAT_MODES, OUTGOING_EVENTS } from '../../constants/liveChat';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { ADDITIONAL_ROLES_ORG } from '../../constants/main';

const TABS = {
  AGENTS: 'agents',
  GROUPS: 'groups'
};

const INVITE_MODES = {
  INVITATION: 'invitation',
  TRANSFER: 'transfer'
};

const InviteAgentsPanel = ({
  transferAndLeave = false,
  isInactive = false,
  hasJoined = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const { Text } = Typography;
  const [searchParams] = useSearchParams();

  const { liveAgentGroups } = useContext(LiveChatContext);
  const { isTransferringConversation } = useSelector((state) => state.livechat);
  const { currentUser } = useSelector((state) => state.auth);

  const { data: livechatSettings } = getCurrentQueryState('fetchLiveChatApplicationSettings', { orgId });
  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: usersThinList } = getCurrentQueryState('fetchUsersThinList', { orgId, urlParams: { organizationRole: ADDITIONAL_ROLES_ORG.LIVE_AGENT } });

  const [filteredAgentList, setFilteredAgentList] = useState([]);
  const [filteredUserGroupsList, setFilteredUserGroupsList] = useState([]);

  const [tab, setTab] = useState(TABS.AGENTS);
  const [targetToInvite, setTargetToInvite] = useState(null);
  const [search, setSearch] = useState('');

  let filterTimeout = 0;

  useEffect(() => {
    if (activeConversationInfo) {
      setFilteredAgentList((usersThinList ?? [])
        .filter(agent => agent.id !== currentUser.id)
        .filter(agent => agent.fullName.toLowerCase().includes(search.toLowerCase()) || agent.email.toLowerCase().includes(search.toLowerCase())));
    }
  }, [usersThinList, activeConversationInfo?.livechatParticipants]);

  useEffect(() => {
    setFilteredUserGroupsList(liveAgentGroups.filter(group => group.name.toLowerCase().includes(search.toLowerCase())));
  }, [liveAgentGroups]);

  useEffect(() => {
    if (typeof search === 'string') {
      setFilteredAgentList(
        (usersThinList ?? [])
          .filter(agent => agent.id !== currentUser.id)
          .filter(agent => agent.fullName.toLowerCase().includes(search.toLowerCase()) || agent.email.toLowerCase().includes(search.toLowerCase()))
      );

      setFilteredUserGroupsList(
        liveAgentGroups.filter(group => group.name.toLowerCase().includes(search.toLowerCase()))
      );
    }
  }, [search]);

  const isOwner = useMemo(() => {
    return activeConversationInfo?.livechatParticipants.find(participant => participant.platformUserId === currentUser.id && participant.isOwner);
  }, [activeConversationInfo?.livechatParticipants]);

  const onInvite = (mode = INVITE_MODES.INVITATION, leaveOnAccept = false) => {
    let valuesToSubmit = {
      type: OUTGOING_EVENTS.REQUEST_CREATE,
      data: {
        livechatConversation: {
          id: activeConversationInfo.id
        },
        type: mode,
        leaveOnAccept: leaveOnAccept
      }
    };

    if (targetToInvite.fullName) {
      valuesToSubmit.data.targetUserId = targetToInvite.id;
    } else {
      valuesToSubmit.data.targetGroupId = targetToInvite.id;
    }

    dispatch(transferConversation(valuesToSubmit, livechatSettings?.livechatMode === LIVECHAT_MODES.PUBLIC));
  };

  return (
    <Row
      wrap={false}
      className="flex-column h-100 pb-2"
    >
      <Col flex="auto">
        <Row
          wrap={false}
          className="flex-column h-100"
        >
          {
            transferAndLeave ? (
              <Text className="fs-sm mb-3" type="secondary">
                {t('You are the owner of this conversation. To leave you have to transfer it first to another LiveChat Agent.')}
              </Text>
            ) : null
          }

          <Col className="mb-4 mx-auto">
            <Radio.Group
              value={tab}
              onChange={({ target }) => setTab(target.value)}
              className="invite-panel-group"
              buttonStyle="solid"
            >
              <Radio.Button value={TABS.AGENTS}>
                {t('LiveChat Agents')}
              </Radio.Button>

              <Radio.Button value={TABS.GROUPS}>
                {t('Groups')}
              </Radio.Button>
            </Radio.Group>
          </Col>

          <Col
            flex="none"
            className="mb-4"
          >
            <Input
              size="large"
              prefix={<SearchOutlined className="text-muted" />}
              placeholder={`${t('Search')} ${tab === TABS.AGENTS ? t('LiveChat Agents') : t('groups')}`}
              onChange={({ target }) => {
                clearTimeout(filterTimeout);
                filterTimeout = setTimeout(() => {
                  setSearch(target.value);
                }, 350);
              }}
            />
          </Col>

          <Col
            flex="auto"
            className="d-flex flex-column"
          >
            <Row
              wrap={false}
              className="flex-column flex-auto overflow-y-auto overflow-x-hidden"
              style={{ width: 'calc(100% + 32px)', height: 0, marginLeft: -16, backgroundColor: '#f9f9f9' }}
            >
              <List
                rowKey="id"
                className=""
                dataSource={tab === TABS.AGENTS ? filteredAgentList : filteredUserGroupsList}
                renderItem={(item) => (
                  <Row
                    wrap={false}
                    align="middle"
                    gutter={8}
                    className="p-3 agent-item"
                    onClick={() => setTargetToInvite(item.id !== targetToInvite?.id ? item : null)}
                  >
                    <Col>
                      <Avatar
                        size={24}
                        className="fs-base"
                        icon={tab === TABS.GROUPS ? <TeamOutlined /> : null}
                      >
                        {tab === TABS.AGENTS && formatEndUserName(item.fullName)}
                      </Avatar>
                    </Col>

                    <Col flex="auto">
                      <Typography.Text
                        ellipsis
                        className="text-muted"
                      >
                        <Typography.Text>
                          {tab === TABS.GROUPS ? item.name : item.fullName}
                        </Typography.Text>

                        {
                          tab === TABS.AGENTS ? (
                            <Typography.Text
                              className="text-muted fs-sm ml-2"
                            >
                              {item.email}
                            </Typography.Text>
                          ) : null
                        }
                      </Typography.Text>
                    </Col>

                    {
                      targetToInvite?.id === item.id ? (
                        <Col>
                          <CheckCircleFilled className="text-primary fs-lg"/>
                        </Col>
                      ) : null
                    }
                  </Row>
                )}
              />
            </Row>
          </Col>
        </Row>
      </Col>

      <Col
        flex="0 0 44px"
        className="mt-3 mb-1"
      >
        <Row
          wrap={false}
          align="middle"
          gutter={8}
          className="bg-body p-2 m-auto"
          style={{ borderRadius: 9999 }}
        >
          {
            targetToInvite ? (
              <Fragment>
                <Col>
                  <Avatar
                    size={24}
                    className="fs-base"
                    icon={!targetToInvite.fullName ? <TeamOutlined /> : null}
                  >
                    {targetToInvite.fullName && formatEndUserName(targetToInvite.fullName)}
                  </Avatar>
                </Col>

                <Col flex="auto">
                  <Typography.Text
                    ellipsis
                    className="text-muted"
                  >
                    <Typography.Text>
                      {!targetToInvite.fullName ? targetToInvite.name : targetToInvite.fullName}
                    </Typography.Text>

                    {
                      targetToInvite.email ? (
                        <Typography.Text className="text-muted fs-sm ml-2">
                          {targetToInvite.email}
                        </Typography.Text>
                      ) : null
                    }
                  </Typography.Text>
                </Col>

                <Col>
                  <CloseCircleFilled
                    className="text-muted fs-lg cursor-pointer"
                    onClick={() => setTargetToInvite(null)}
                  />
                </Col>
              </Fragment>
            ) : (
              <Fragment>
                <Col>
                  <Text className="text-muted">
                    {t('Select a LiveChat Agent or group')}
                  </Text>
                </Col>

              </Fragment>
            )
          }
        </Row>
      </Col>

      <Col>
        {
          transferAndLeave ? (
            <Tooltip
              title={
                (activeConversationInfo && isInactive)
                  ? t('You can\'t transfer a finished conversation.')
                  : activeConversationInfo && !isOwner ? t('Only the conversation owner can transfer a conversation.') : ''
              }
              trigger={(activeConversationInfo && isInactive) || !isOwner ? 'hover' : ''}
              destroyTooltipOnHide
              mouseEnterDelay={0.5}
            >
              <Button
                type="primary"
                size="large"
                block
                className="mb-2"
                loading={isTransferringConversation}
                disabled={!isOwner || !activeConversationInfo || !targetToInvite || isInactive || !hasJoined}
                onClick={() => onInvite(INVITE_MODES.TRANSFER, true)}
              >
                {t('Transfer to LiveChat Agent & Leave')}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                (activeConversationInfo && isInactive)
                  ? t('You can\'t transfer a finished conversation.')
                  : activeConversationInfo && !isOwner ? t('Only the conversation owner can transfer a conversation.') : ''
              }
              trigger={(activeConversationInfo && isInactive) || !isOwner ? 'hover' : ''}
              destroyTooltipOnHide
              mouseEnterDelay={0.5}
            >
              <Button
                type="primary"
                size="large"
                block
                className="mb-2"
                loading={isTransferringConversation}
                disabled={!isOwner || !activeConversationInfo || !targetToInvite || isInactive || !hasJoined}
                onClick={() => onInvite(INVITE_MODES.TRANSFER)}
              >
                {t('Transfer')}
              </Button>
            </Tooltip>
          )
        }

        {
          livechatSettings?.livechatMode === LIVECHAT_MODES.PUBLIC ? (
            <Tooltip
              title={t('You can\'t invite users to a finished conversation.')}
              trigger={activeConversationInfo && isInactive ? 'hover' : ''}
              destroyTooltipOnHide
              mouseEnterDelay={0.5}
            >
              <Button
                type="primary"
                size="large"
                block
                loading={isTransferringConversation}
                disabled={!activeConversationInfo || !targetToInvite || isInactive || !hasJoined}
                onClick={() => onInvite(INVITE_MODES.INVITATION)}
              >
                {
                  targetToInvite
                    ? (targetToInvite.fullName ? t('Invite LiveChat Agent') : t('Invite Group'))
                    : (tab === TABS.AGENTS ? t('Invite LiveChat Agent') : t('Invite Group'))
                }
              </Button>
            </Tooltip>
          ) : null
        }
      </Col>
    </Row>
  );
};

InviteAgentsPanel.propTypes = {
  transferAndLeave: PropTypes.bool,
  isInactive: PropTypes.bool,
  hasJoined: PropTypes.bool
};

export default InviteAgentsPanel;
